// ============================================
// OFFICEFORGED — Card Engine
// The brain. Every piece of content is a card.
// Cards have names, types, tags, and references.
// Cards persist in IndexedDB (via localForage)
// and flow between Docs, Grid, and PDF.
// ============================================

let cardIdCounter = 0;

// All valid card types
const CARD_TYPES = [
  'text', 'heading', 'table', 'image',
  'snippet', 'spreadsheet', 'chart', 'calculation',
  'signature', 'date-stamp', 'template', 'lookup'
];

export class CardEngine {
  constructor() {
    // cardId → Card object (document cards — live in current session)
    this.cards = new Map();
    // name → cardId (for fast lookup by name)
    this.nameIndex = new Map();
    // tag → Set of cardIds
    this.tagIndex = new Map();
    // Listeners for card changes
    this.listeners = [];
    // Auto-name counters per type
    this.typeCounters = {};
    CARD_TYPES.forEach(t => this.typeCounters[t] = 0);

    // Library: persistent cards saved to IndexedDB
    this.library = new Map(); // cardId → Card
    this.libraryNameIndex = new Map(); // name → cardId
    this._libraryLoaded = false;

    // Grid reference (set by renderer.js after grid init)
    this._gridModule = null;

    // Load library from storage on boot
    this._loadLibrary();
  }

  // ── Document Cards (session lifetime) ──

  createCard(type, opts = {}) {
    const id = `card_${++cardIdCounter}`;
    this.typeCounters[type] = (this.typeCounters[type] || 0) + 1;
    const defaultName = opts.name || `${this._capitalize(type)}${this.typeCounters[type]}`;

    const card = {
      id,
      type,
      name: defaultName,
      tags: opts.tags || [],
      data: opts.data || {},
      preview: opts.preview || '',
      pmNodeId: opts.pmNodeId || null,
      libraryRef: opts.libraryRef || null, // links back to library card
      created: Date.now(),
      modified: Date.now()
    };

    this.cards.set(id, card);
    this.nameIndex.set(defaultName.toLowerCase(), id);
    card.tags.forEach(tag => this._addToTagIndex(tag, id));
    this._emit('card:created', card);
    return card;
  }

  updateCard(id, updates) {
    const card = this.cards.get(id);
    if (!card) return null;

    if (updates.name && updates.name !== card.name) {
      this.nameIndex.delete(card.name.toLowerCase());
      this.nameIndex.set(updates.name.toLowerCase(), id);
    }

    if (updates.tags) {
      card.tags.forEach(tag => this._removeFromTagIndex(tag, id));
      updates.tags.forEach(tag => this._addToTagIndex(tag, id));
    }

    Object.assign(card, updates, { modified: Date.now() });
    this._emit('card:updated', card);
    return card;
  }

  removeCard(id) {
    const card = this.cards.get(id);
    if (!card) return;
    this.nameIndex.delete(card.name.toLowerCase());
    card.tags.forEach(tag => this._removeFromTagIndex(tag, id));
    this.cards.delete(id);
    this._emit('card:removed', card);
  }

  getCard(id) {
    return this.cards.get(id) || null;
  }

  getCardByName(name) {
    // Check document cards first, then library
    const id = this.nameIndex.get(name.toLowerCase());
    if (id) return this.cards.get(id) || null;
    const libId = this.libraryNameIndex.get(name.toLowerCase());
    if (libId) return this.library.get(libId) || null;
    return null;
  }

  getAllCards() {
    // Return both session cards AND library cards (deduplicated by name)
    const seen = new Set();
    const result = [];
    for (const card of this.cards.values()) {
      if (!seen.has(card.name?.toLowerCase())) {
        seen.add(card.name?.toLowerCase());
        result.push(card);
      }
    }
    for (const card of this.library.values()) {
      if (!seen.has(card.name?.toLowerCase())) {
        seen.add(card.name?.toLowerCase());
        result.push(card);
      }
    }
    return result;
  }

  clear() {
    this.cards.clear();
    this.nameIndex.clear();
    this.tagIndex.clear();
    CARD_TYPES.forEach(t => this.typeCounters[t] = 0);
    this._emit('cards:cleared', null);
  }

  getCardsByType(type) {
    return this.getAllCards().filter(c => c.type === type);
  }

  getCardsByTag(tag) {
    const ids = this.tagIndex.get(tag.toLowerCase()) || new Set();
    return Array.from(ids).map(id => this.cards.get(id)).filter(Boolean);
  }

  addTag(cardId, tag) {
    const card = this.cards.get(cardId) || this.library.get(cardId);
    if (!card || card.tags.includes(tag)) return;
    card.tags.push(tag);
    if (this.cards.has(cardId)) this._addToTagIndex(tag, cardId);
    card.modified = Date.now();
    this._emit('card:updated', card);
    if (this.library.has(cardId)) this._saveLibrary();
  }

  removeTag(cardId, tag) {
    const card = this.cards.get(cardId) || this.library.get(cardId);
    if (!card) return;
    card.tags = card.tags.filter(t => t !== tag);
    if (this.cards.has(cardId)) this._removeFromTagIndex(tag, cardId);
    card.modified = Date.now();
    this._emit('card:updated', card);
    if (this.library.has(cardId)) this._saveLibrary();
  }

  renameCard(cardId, newName) {
    const existing = this.nameIndex.get(newName.toLowerCase())
      || this.libraryNameIndex.get(newName.toLowerCase());
    if (existing && existing !== cardId) return false;

    if (this.cards.has(cardId)) {
      return this.updateCard(cardId, { name: newName });
    } else if (this.library.has(cardId)) {
      return this.updateLibraryCard(cardId, { name: newName });
    }
    return false;
  }

  // ── Library Cards (persistent, reusable) ──

  saveToLibrary(card) {
    // Save a copy of a card to the persistent library
    const libCard = {
      ...card,
      id: card.id.startsWith('lib_') ? card.id : `lib_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      savedAt: new Date().toISOString(),
      modified: Date.now(),
    };

    // Remove ProseMirror-specific fields
    delete libCard.pmNodeId;

    this.library.set(libCard.id, libCard);
    this.libraryNameIndex.set(libCard.name.toLowerCase(), libCard.id);
    this._saveLibrary();
    this._emit('library:saved', libCard);
    return libCard;
  }

  createLibraryCard(type, opts = {}) {
    const id = `lib_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const name = opts.name || `${this._capitalize(type)} Card`;

    // Check for name collision
    if (this.libraryNameIndex.has(name.toLowerCase())) {
      const baseName = name;
      let counter = 2;
      while (this.libraryNameIndex.has(`${baseName} ${counter}`.toLowerCase())) counter++;
      opts.name = `${baseName} ${counter}`;
    }

    const card = {
      id,
      type,
      name: opts.name || name,
      tags: opts.tags || [],
      data: opts.data || {},
      preview: opts.preview || '',
      savedAt: new Date().toISOString(),
      created: Date.now(),
      modified: Date.now(),
    };

    this.library.set(id, card);
    this.libraryNameIndex.set(card.name.toLowerCase(), id);
    this._saveLibrary();
    this._emit('library:saved', card);
    return card;
  }

  updateLibraryCard(id, updates) {
    const card = this.library.get(id);
    if (!card) return null;

    if (updates.name && updates.name !== card.name) {
      this.libraryNameIndex.delete(card.name.toLowerCase());
      this.libraryNameIndex.set(updates.name.toLowerCase(), id);
    }

    Object.assign(card, updates, { modified: Date.now() });
    this._saveLibrary();
    this._emit('library:updated', card);
    return card;
  }

  removeLibraryCard(id) {
    const card = this.library.get(id);
    if (!card) return;
    this.libraryNameIndex.delete(card.name.toLowerCase());
    this.library.delete(id);
    this._saveLibrary();
    this._emit('library:removed', card);
  }

  getLibraryCard(id) {
    return this.library.get(id) || null;
  }

  getLibraryCardByName(name) {
    const id = this.libraryNameIndex.get(name.toLowerCase());
    return id ? this.library.get(id) : null;
  }

  getAllLibraryCards() {
    return Array.from(this.library.values());
  }

  getLibraryCardsByType(type) {
    return this.getAllLibraryCards().filter(c => c.type === type);
  }

  getLibraryCardsByTag(tag) {
    return this.getAllLibraryCards().filter(c =>
      c.tags.some(t => t.toLowerCase() === tag.toLowerCase())
    );
  }

  searchLibrary(query) {
    if (!query) return this.getAllLibraryCards();
    const q = query.toLowerCase();
    return this.getAllLibraryCards().filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.type.toLowerCase().includes(q) ||
      c.tags.some(t => t.toLowerCase().includes(q)) ||
      (c.preview && c.preview.toLowerCase().includes(q))
    );
  }

  // Insert a library card into the document as a document card
  insertFromLibrary(libraryCardId) {
    const libCard = this.library.get(libraryCardId);
    if (!libCard) return null;

    return this.createCard(libCard.type, {
      name: libCard.name,
      tags: [...libCard.tags],
      data: JSON.parse(JSON.stringify(libCard.data)),
      preview: libCard.preview,
      libraryRef: libraryCardId,
    });
  }

  // ── Persistence (localForage / IndexedDB) ──

  async _saveLibrary() {
    try {
      const data = Array.from(this.library.values());
      if (window.localforage) {
        await window.localforage.setItem('ofg_card_library', data);
      } else {
        localStorage.setItem('ofg_card_library', JSON.stringify(data));
      }
    } catch (e) {
      console.error('CardEngine: Failed to save library:', e);
    }
  }

  async _loadLibrary() {
    try {
      let data = null;
      if (window.localforage) {
        data = await window.localforage.getItem('ofg_card_library');
      } else {
        const raw = localStorage.getItem('ofg_card_library');
        if (raw) data = JSON.parse(raw);
      }

      if (data && Array.isArray(data)) {
        data.forEach(card => {
          this.library.set(card.id, card);
          this.libraryNameIndex.set(card.name.toLowerCase(), card.id);
        });
        this._libraryLoaded = true;
        this._emit('library:loaded', { count: data.length });
      }
    } catch (e) {
      console.error('CardEngine: Failed to load library:', e);
    }
  }

  // ── Reference Resolution ──

  resolveReference(refString) {
    // Support {CardName(arg)} syntax for lookup cards
    const lookupMatch = refString.match(/^\{([^(}]+)\(([^)]*)\)\}$/);
    if (lookupMatch) {
      const cardName = lookupMatch[1].trim();
      const inputValue = lookupMatch[2].trim();
      const card = this.getCardByName(cardName);
      if (!card || card.type !== 'lookup') return { resolved: false, value: `{${cardName}?}` };
      const result = this.resolveLookup(card, inputValue);
      return result;
    }

    const match = refString.match(/^\{([^.}]+)(?:\.([^}]+))?\}$/);
    if (!match) return { resolved: false, value: refString };

    const cardName = match[1].trim();
    const field = match[2] ? match[2].trim() : null;

    // Check document cards, then library
    const card = this.getCardByName(cardName);
    if (!card) return { resolved: false, value: `{${cardName}?}` };

    // No field — return preview or full content
    if (!field) {
      if (card.type === 'snippet') {
        return { resolved: true, value: card.data.content || card.preview || card.name, card };
      }
      if (card.type === 'date-stamp') {
        return { resolved: true, value: this._resolveDateStamp(card), card };
      }
      if (card.type === 'calculation') {
        return { resolved: true, value: this._resolveCalculation(card), card };
      }
      // Grid-linked named range — resolve live value
      if (card.type === 'spreadsheet' && card.data.gridLinked && this._gridModule) {
        const liveValue = this._gridModule.getNamedRangeValue(card.name);
        if (liveValue !== null) {
          return { resolved: true, value: liveValue, card };
        }
      }
      return { resolved: true, value: card.preview || card.name, card };
    }

    // Table/spreadsheet cell reference (e.g., A1, B3)
    if ((card.type === 'table' || card.type === 'spreadsheet') && card.data.cells) {
      const cellMatch = field.match(/^([A-Z]+)(\d+)$/i);
      if (cellMatch) {
        const col = this._colToIndex(cellMatch[1]);
        const row = parseInt(cellMatch[2]) - 1;
        const cellValue = card.data.cells[row] && card.data.cells[row][col];
        if (cellValue !== undefined && cellValue !== null) {
          return { resolved: true, value: String(cellValue), card };
        }
        return { resolved: false, value: `{${cardName}.${field}?}` };
      }
    }

    // Spreadsheet card — try to pull from live Grid module
    if (card.type === 'spreadsheet' && this._gridModule) {
      const gridValue = this._resolveGridReference(card, field);
      if (gridValue !== null) {
        return { resolved: true, value: String(gridValue), card };
      }
    }

    // Named data fields (e.g., {Patient.name}, {Patient.dob})
    if (card.data && card.data[field] !== undefined) {
      return { resolved: true, value: String(card.data[field]), card };
    }

    return { resolved: false, value: `{${cardName}.${field}?}` };
  }

  _resolveDateStamp(card) {
    const fmt = card.data.format || 'locale';
    const now = new Date();
    if (fmt === 'iso') return now.toISOString().split('T')[0];
    if (fmt === 'us') return now.toLocaleDateString('en-US');
    if (fmt === 'eu') return now.toLocaleDateString('en-GB');
    if (fmt === 'long') return now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    if (fmt === 'datetime') return now.toLocaleString();
    return now.toLocaleDateString();
  }

  _resolveCalculation(card) {
    const formula = card.data.formula || '';
    // Simple aggregation: SUM, AVG, COUNT over a card reference
    const aggMatch = formula.match(/^(SUM|AVG|COUNT|MIN|MAX)\(([^)]+)\)$/i);
    if (aggMatch) {
      const fn = aggMatch[1].toUpperCase();
      const ref = aggMatch[2].trim();
      const refResult = this.resolveReference(`{${ref}}`);
      if (refResult.resolved && refResult.card) {
        const cells = refResult.card.data.cells;
        if (cells) {
          const nums = cells.flat().map(Number).filter(n => !isNaN(n));
          if (fn === 'SUM') return nums.reduce((a, b) => a + b, 0).toFixed(2);
          if (fn === 'AVG') return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2);
          if (fn === 'COUNT') return String(nums.length);
          if (fn === 'MIN') return String(Math.min(...nums));
          if (fn === 'MAX') return String(Math.max(...nums));
        }
      }
    }
    return formula;
  }

  _resolveGridReference(card, field) {
    if (!this._gridModule || !this._gridModule.univerAPI) return null;
    try {
      const wb = this._gridModule.univerAPI.getActiveWorkbook();
      if (!wb) return null;
      const sheet = wb.getActiveSheet();
      if (!sheet) return null;
      const cellMatch = field.match(/^([A-Z]+)(\d+)$/i);
      if (cellMatch) {
        const col = this._colToIndex(cellMatch[1]);
        const row = parseInt(cellMatch[2]) - 1;
        const range = sheet.getRange(row, col, 1, 1);
        return range.getValue();
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  _colToIndex(col) {
    let idx = 0;
    for (let i = 0; i < col.length; i++) {
      idx = idx * 26 + (col.charCodeAt(i) - 64);
    }
    return idx - 1;
  }

  // ── Lookup Card Support ──

  resolveLookup(card, inputValue) {
    if (!card || card.type !== 'lookup') return { resolved: false, value: '?' };

    // Use explicit feed/result columns if stored, otherwise fall back to range edges
    const feedCol = card.data?.feedCol;
    const resultCol = card.data?.resultCol;

    // Try live grid first
    if (this._gridModule && this._gridModule.univerAPI) {
      try {
        const wb = this._gridModule.univerAPI.getActiveWorkbook();
        const sheet = wb ? wb.getActiveSheet() : null;
        const tableRange = card.data.tableRange;
        if (sheet && tableRange) {
          const parts = tableRange.split(':');
          const start = this._parseCell(parts[0]);
          const end = this._parseCell(parts[1]);
          if (start && end) {
            const inCol = feedCol != null ? feedCol : start.col;
            const outCol = resultCol != null ? resultCol : end.col;
            for (let r = start.row; r <= end.row; r++) {
              const cellVal = sheet.getRange(r, inCol, 1, 1).getValue();
              if (cellVal != null && String(cellVal).toLowerCase() === inputValue.toLowerCase()) {
                const outputVal = sheet.getRange(r, outCol, 1, 1).getValue();
                return {
                  resolved: true,
                  value: `${cellVal} | ${outputVal != null ? outputVal : ''}`,
                  inputValue: String(cellVal),
                  outputValue: outputVal != null ? String(outputVal) : '',
                  card
                };
              }
            }
          }
        }
      } catch (e) { /* fall through to cache */ }
    }

    // Fall back to cached values on the card
    const cached = card.data?.cachedValues;
    if (cached && cached.length > 0) {
      const match = cached.find(v => v.input.toLowerCase() === inputValue.toLowerCase());
      if (match) {
        return {
          resolved: true,
          value: `${match.input} | ${match.output}`,
          inputValue: match.input,
          outputValue: match.output,
          card
        };
      }
      return { resolved: false, value: `${inputValue} | not found`, card };
    }

    return { resolved: false, value: `${inputValue} | ?`, card };
  }

  // Get all available input values for a lookup card
  getLookupValues(card) {
    if (!card || card.type !== 'lookup') return [];

    const feedCol = card.data?.feedCol;
    const resultCol = card.data?.resultCol;

    // Try live grid first
    if (this._gridModule && this._gridModule.univerAPI) {
      try {
        const wb = this._gridModule.univerAPI.getActiveWorkbook();
        const sheet = wb ? wb.getActiveSheet() : null;
        const tableRange = card.data.tableRange;
        if (sheet && tableRange) {
          const parts = tableRange.split(':');
          const start = this._parseCell(parts[0]);
          const end = this._parseCell(parts[1]);
          if (start && end) {
            const inCol = feedCol != null ? feedCol : start.col;
            const outCol = resultCol != null ? resultCol : end.col;
            const results = [];
            for (let r = start.row; r <= end.row; r++) {
              const inputVal = sheet.getRange(r, inCol, 1, 1).getValue();
              const outputVal = sheet.getRange(r, outCol, 1, 1).getValue();
              if (inputVal != null && String(inputVal).trim() !== '') {
                results.push({ input: String(inputVal), output: outputVal != null ? String(outputVal) : '' });
              }
            }
            if (results.length > 0) return results;
          }
        }
      } catch (e) { /* fall through to cache */ }
    }

    // Fall back to cached values on the card
    return card.data?.cachedValues || [];
  }

  _parseCell(ref) {
    const m = ref.match(/^([A-Z]+)(\d+)$/i);
    if (!m) return null;
    let col = 0;
    for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].toUpperCase().charCodeAt(i) - 64);
    return { col: col - 1, row: parseInt(m[2]) - 1 };
  }

  _indexToCol(idx) {
    let result = '';
    idx += 1;
    while (idx > 0) {
      idx--;
      result = String.fromCharCode(65 + (idx % 26)) + result;
      idx = Math.floor(idx / 26);
    }
    return result;
  }

  // ── Template Support ──

  // Create a document from a template card
  instantiateTemplate(templateCardId) {
    const template = this.library.get(templateCardId) || this.cards.get(templateCardId);
    if (!template || template.type !== 'template') return null;

    const slots = template.data.slots || [];
    const instances = [];

    slots.forEach(slot => {
      if (slot.libraryRef) {
        // Insert from library
        const card = this.insertFromLibrary(slot.libraryRef);
        if (card) instances.push(card);
      } else if (slot.type) {
        // Create a new card of the specified type
        const card = this.createCard(slot.type, {
          name: slot.name || undefined,
          data: slot.data || {},
          preview: slot.placeholder || '',
        });
        instances.push(card);
      }
    });

    return instances;
  }

  // ── Events & Utilities ──

  getAllTags() {
    const tags = new Set(this.tagIndex.keys());
    this.library.forEach(card => card.tags.forEach(t => tags.add(t.toLowerCase())));
    return Array.from(tags);
  }

  on(callback) {
    this.listeners.push(callback);
    return () => { this.listeners = this.listeners.filter(l => l !== callback); };
  }

  _emit(event, data) {
    this.listeners.forEach(fn => fn(event, data));
  }

  _addToTagIndex(tag, cardId) {
    const key = tag.toLowerCase();
    if (!this.tagIndex.has(key)) this.tagIndex.set(key, new Set());
    this.tagIndex.get(key).add(cardId);
  }

  _removeFromTagIndex(tag, cardId) {
    const key = tag.toLowerCase();
    const set = this.tagIndex.get(key);
    if (set) {
      set.delete(cardId);
      if (set.size === 0) this.tagIndex.delete(key);
    }
  }

  _capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // ── Built-in Template Presets ──

  getTemplatePresets() {
    return [
      {
        name: 'Medical SOAP Note',
        description: 'Subjective, Objective, Assessment, Plan — standard clinical documentation',
        tags: ['medical', 'clinical', 'healthcare'],
        slots: [
          { type: 'heading', name: 'SOAP Note', data: { content: 'SOAP Note' }, placeholder: 'SOAP Note' },
          { type: 'date-stamp', name: 'Visit Date', data: { format: 'long' }, placeholder: 'Date of visit' },
          { type: 'snippet', name: 'Patient Info', data: { content: 'Patient: ___\nDOB: ___\nMRN: ___' }, placeholder: 'Patient demographics' },
          { type: 'heading', name: 'Subjective', data: { content: 'Subjective', level: 2 }, placeholder: 'Subjective' },
          { type: 'snippet', name: 'Chief Complaint', data: { content: 'Chief Complaint: ___\nHPI: ___\nROS: ___' }, placeholder: 'Patient-reported symptoms' },
          { type: 'heading', name: 'Objective', data: { content: 'Objective', level: 2 }, placeholder: 'Objective' },
          { type: 'snippet', name: 'Vitals', data: { content: 'BP: ___  HR: ___  Temp: ___  RR: ___  SpO2: ___' }, placeholder: 'Vital signs' },
          { type: 'snippet', name: 'Physical Exam', data: { content: 'General: ___\nHEENT: ___\nCardiovascular: ___\nRespiratory: ___\nAbdomen: ___\nExtremities: ___\nNeuro: ___' }, placeholder: 'Physical examination findings' },
          { type: 'heading', name: 'Assessment', data: { content: 'Assessment', level: 2 }, placeholder: 'Assessment' },
          { type: 'snippet', name: 'Diagnosis', data: { content: 'Primary Diagnosis: ___\nICD-10: ___\nDifferential: ___' }, placeholder: 'Clinical assessment' },
          { type: 'heading', name: 'Plan', data: { content: 'Plan', level: 2 }, placeholder: 'Plan' },
          { type: 'snippet', name: 'Treatment Plan', data: { content: 'Medications: ___\nProcedures: ___\nLabs: ___\nImaging: ___\nReferrals: ___\nFollow-up: ___' }, placeholder: 'Treatment plan' },
          { type: 'signature', name: 'Provider Signature', data: {}, placeholder: 'Provider signature' },
        ]
      },
      {
        name: 'Invoice',
        description: 'Standard business invoice with line items and totals',
        tags: ['business', 'finance', 'billing'],
        slots: [
          { type: 'heading', name: 'Invoice Header', data: { content: 'INVOICE' }, placeholder: 'INVOICE' },
          { type: 'date-stamp', name: 'Invoice Date', data: { format: 'short' }, placeholder: 'Invoice date' },
          { type: 'snippet', name: 'Invoice Number', data: { content: 'Invoice #: ___' }, placeholder: 'Invoice number' },
          { type: 'snippet', name: 'Bill From', data: { content: 'From:\n___\n___\n___' }, placeholder: 'Sender info' },
          { type: 'snippet', name: 'Bill To', data: { content: 'Bill To:\n___\n___\n___' }, placeholder: 'Recipient info' },
          { type: 'table', name: 'Line Items', data: { headers: ['Description', 'Qty', 'Rate', 'Amount'], rows: [['___', '___', '___', '___']] }, placeholder: 'Line items table' },
          { type: 'snippet', name: 'Invoice Totals', data: { content: 'Subtotal: $___\nTax: $___\nTotal: $___' }, placeholder: 'Totals' },
          { type: 'snippet', name: 'Payment Terms', data: { content: 'Payment Terms: Net 30\nPayment Method: ___\nNotes: ___' }, placeholder: 'Payment terms' },
        ]
      },
      {
        name: 'Contract Agreement',
        description: 'Basic contract with parties, terms, and signature blocks',
        tags: ['legal', 'business', 'contract'],
        slots: [
          { type: 'heading', name: 'Contract Title', data: { content: 'SERVICE AGREEMENT' }, placeholder: 'Contract title' },
          { type: 'date-stamp', name: 'Effective Date', data: { format: 'long' }, placeholder: 'Effective date' },
          { type: 'snippet', name: 'Parties', data: { content: 'This Agreement is entered into by and between:\n\nParty A: ___\nAddress: ___\n\nParty B: ___\nAddress: ___' }, placeholder: 'Contracting parties' },
          { type: 'heading', name: 'Scope', data: { content: 'Scope of Work', level: 2 }, placeholder: 'Scope' },
          { type: 'snippet', name: 'Scope Details', data: { content: '___' }, placeholder: 'Description of services' },
          { type: 'heading', name: 'Compensation', data: { content: 'Compensation', level: 2 }, placeholder: 'Compensation' },
          { type: 'snippet', name: 'Payment Details', data: { content: 'Total Fee: $___\nPayment Schedule: ___\nLate Fee: ___' }, placeholder: 'Payment terms' },
          { type: 'heading', name: 'Term', data: { content: 'Term & Termination', level: 2 }, placeholder: 'Term' },
          { type: 'snippet', name: 'Duration', data: { content: 'Start Date: ___\nEnd Date: ___\nTermination Notice: ___ days' }, placeholder: 'Duration and termination' },
          { type: 'heading', name: 'Signatures', data: { content: 'Signatures', level: 2 }, placeholder: 'Signatures' },
          { type: 'signature', name: 'Party A Signature', data: {}, placeholder: 'Party A signature' },
          { type: 'signature', name: 'Party B Signature', data: {}, placeholder: 'Party B signature' },
        ]
      },
      {
        name: 'Meeting Minutes',
        description: 'Standard meeting minutes with attendees, agenda, and action items',
        tags: ['business', 'meetings', 'admin'],
        slots: [
          { type: 'heading', name: 'Meeting Title', data: { content: 'Meeting Minutes' }, placeholder: 'Meeting title' },
          { type: 'date-stamp', name: 'Meeting Date', data: { format: 'long' }, placeholder: 'Meeting date' },
          { type: 'snippet', name: 'Attendees', data: { content: 'Attendees:\n- ___\n- ___\n- ___' }, placeholder: 'Who attended' },
          { type: 'heading', name: 'Agenda', data: { content: 'Agenda', level: 2 }, placeholder: 'Agenda' },
          { type: 'snippet', name: 'Agenda Items', data: { content: '1. ___\n2. ___\n3. ___' }, placeholder: 'Agenda items' },
          { type: 'heading', name: 'Discussion', data: { content: 'Discussion', level: 2 }, placeholder: 'Discussion' },
          { type: 'snippet', name: 'Notes', data: { content: '___' }, placeholder: 'Discussion notes' },
          { type: 'heading', name: 'Action Items', data: { content: 'Action Items', level: 2 }, placeholder: 'Action Items' },
          { type: 'snippet', name: 'Actions', data: { content: '- [ ] ___ — Owner: ___ — Due: ___\n- [ ] ___ — Owner: ___ — Due: ___' }, placeholder: 'Action items' },
        ]
      },
      {
        name: 'Project Proposal',
        description: 'Project proposal with objectives, timeline, budget, and deliverables',
        tags: ['business', 'project', 'proposal'],
        slots: [
          { type: 'heading', name: 'Proposal Title', data: { content: 'PROJECT PROPOSAL' }, placeholder: 'Proposal title' },
          { type: 'date-stamp', name: 'Proposal Date', data: { format: 'long' }, placeholder: 'Proposal date' },
          { type: 'snippet', name: 'Prepared By', data: { content: 'Prepared by: ___\nOrganization: ___\nContact: ___' }, placeholder: 'Preparer info' },
          { type: 'heading', name: 'Executive Summary', data: { content: 'Executive Summary', level: 2 }, placeholder: 'Executive Summary' },
          { type: 'snippet', name: 'Summary', data: { content: '___' }, placeholder: 'Brief overview' },
          { type: 'heading', name: 'Objectives', data: { content: 'Objectives', level: 2 }, placeholder: 'Objectives' },
          { type: 'snippet', name: 'Goals', data: { content: '1. ___\n2. ___\n3. ___' }, placeholder: 'Project goals' },
          { type: 'heading', name: 'Timeline', data: { content: 'Timeline', level: 2 }, placeholder: 'Timeline' },
          { type: 'table', name: 'Milestones', data: { headers: ['Phase', 'Deliverable', 'Date'], rows: [['___', '___', '___']] }, placeholder: 'Milestone table' },
          { type: 'heading', name: 'Budget', data: { content: 'Budget', level: 2 }, placeholder: 'Budget' },
          { type: 'table', name: 'Budget Items', data: { headers: ['Item', 'Cost', 'Notes'], rows: [['___', '$___', '___']] }, placeholder: 'Budget breakdown' },
          { type: 'snippet', name: 'Total Budget', data: { content: 'Total Estimated Budget: $___' }, placeholder: 'Total budget' },
        ]
      },
      {
        name: 'Employee Onboarding',
        description: 'New hire onboarding checklist and welcome document',
        tags: ['hr', 'business', 'onboarding'],
        slots: [
          { type: 'heading', name: 'Welcome Header', data: { content: 'Welcome to the Team!' }, placeholder: 'Welcome' },
          { type: 'date-stamp', name: 'Start Date', data: { format: 'long' }, placeholder: 'Employee start date' },
          { type: 'snippet', name: 'Employee Info', data: { content: 'Name: ___\nTitle: ___\nDepartment: ___\nManager: ___' }, placeholder: 'Employee details' },
          { type: 'heading', name: 'First Week', data: { content: 'First Week Checklist', level: 2 }, placeholder: 'First Week' },
          { type: 'snippet', name: 'Week 1 Tasks', data: { content: '- [ ] Complete HR paperwork\n- [ ] Set up workstation\n- [ ] Obtain security badge\n- [ ] Review employee handbook\n- [ ] Meet team members\n- [ ] Complete IT setup (email, VPN, tools)' }, placeholder: 'Week 1 tasks' },
          { type: 'heading', name: 'First Month', data: { content: 'First Month Goals', level: 2 }, placeholder: 'First Month' },
          { type: 'snippet', name: 'Month 1 Goals', data: { content: '1. ___\n2. ___\n3. ___' }, placeholder: 'Month 1 goals' },
          { type: 'snippet', name: 'Key Contacts', data: { content: 'HR: ___\nIT Support: ___\nBuddy/Mentor: ___' }, placeholder: 'Key contacts' },
          { type: 'signature', name: 'Employee Acknowledgment', data: {}, placeholder: 'Employee signature' },
        ]
      },
    ];
  }

  // Install a preset template into the library
  installTemplate(presetName) {
    const presets = this.getTemplatePresets();
    const preset = presets.find(p => p.name === presetName);
    if (!preset) return null;

    // Check if already installed
    const existing = Array.from(this.library.values()).find(c => c.name === preset.name && c.type === 'template');
    if (existing) return existing;

    return this.createLibraryCard('template', {
      name: preset.name,
      tags: preset.tags,
      data: {
        description: preset.description,
        slots: preset.slots,
      },
      preview: preset.description,
    });
  }

  // Install all preset templates
  installAllTemplates() {
    return this.getTemplatePresets().map(p => this.installTemplate(p.name)).filter(Boolean);
  }
}
